import { Alert, Card, Col, Form, Row, Space, Typography, Image, Skeleton, Select, Tag, Badge, Input } from 'antd';
import styled from 'styled-components';
import { InfoCircleOutlined, StarFilled } from '@ant-design/icons';
import { Button } from 'antd-mobile';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import FormSubmit from 'components/form/FormSubmit';
import PageMobile from '../../../components/page-mobile';
import useTranslations from '../../../hooks/useTranslations';
import Control from '../../../components/control/Control';
import DynamicIcon from '../../../components/dynamic-icon';
import TherapistInfo from '../../../components/global/TherapistInfo';
import Location from '../location';
import DefaultAvatar from '../../../assets/img/DefaultAvatar.jpg';
import { useBookingCtx } from './context/BookingContext';
import BookingContextProvider from './context/BookingContextProvider';
import BookingBottomBar from './components/BookingBottomBar';

const { Text } = Typography;

const CardStyled = styled(Card)`
    .ant-card-body {
        padding: 12px;
    }
`;

function Booking() {
    const { t } = useTranslations();
    const { masseusesId, serviceId } = useParams();

    const [visibleMaps, setVisibleMaps] = useState(false);

    const {
        detailTherapist,
        isLoadingDetailTherapist,
        isLoadingServiceOptions,
        servicesOptions,
        therapistOptions,
        isLoadingTherapistOptions,
        formInstance,
    } = useBookingCtx();

    const textLocationOrder = t('LocationOrder', 'Location Order');
    const textServiceDetail = t('ServiceDetail', 'Service Detail');
    const textAdditionalInformation = t('AdditionalInformation', 'Additional Information');
    const textLocation = t('Location', 'Location');
    const textNotes = t('Notes', 'Notes');
    const textName = t('Name', 'Name');
    const textPhoneNumber = t('PhoneNumber', 'Phone Number');
    const textService = t('Service', 'Service');
    const textTherapist = t('Therapist', 'Therapist');
    const textBook = t('Book', 'Book');
    const textSelect = t('Select', 'Select');

    const lat = Form.useWatch('lat', formInstance);
    const long = Form.useWatch('long', formInstance);

    const renderTherapistSelectOption = (item) => {
        return (
            <Select.Option style={{ textAlign: 'unset' }} value={item?.value} key={item?.value} label={item?.label}>
                <Card value={item?.value} key={item?.value} label={item?.label} bodyStyle={{ padding: 12 }}>
                    <Row align="middle">
                        <Col flex="auto">
                            <Space>
                                <Image
                                    style={{
                                        borderRadius: 4,
                                        objectFit: 'cover',
                                    }}
                                    src={item?.profile_picture}
                                    width={48}
                                    height={48}
                                    onError={(event) => {
                                        const newSource = DefaultAvatar;
                                        event.target.src = newSource;
                                    }}
                                />

                                <div>
                                    <Space direction="vertical" className="therapist-detail">
                                        <Text>{item?.full_name}</Text>
                                        <div>
                                            <Space split={<Badge status="default" />}>
                                                <Tag color="warning" style={{ marginRight: 'unset' }} icon={<StarFilled />}>
                                                    {item?.rating}
                                                </Tag>
                                                <div>
                                                    <DynamicIcon iconName="mappin" width={14} height={14} />
                                                    <Text>{item?.distance_km} Km</Text>
                                                </div>
                                            </Space>
                                        </div>
                                    </Space>
                                </div>
                            </Space>
                        </Col>
                        <Col flex="none">
                            <Button color="primary">{textBook}</Button>
                        </Col>
                    </Row>
                </Card>
            </Select.Option>
        );
    };

    const renderServiceSelectOption = (item) => (
        <Card bodyStyle={{ padding: 12 }} key={item?.id} value={item?.id} label={item?.label} a>
            <Row align="middle">
                <Col flex="auto">
                    <Space style={{ width: '100%' }}>
                        <Image
                            style={{ borderRadius: 4, objectFit: 'cover' }}
                            src={item?.image}
                            width={48}
                            height={48}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DefaultAvatar;
                            }}
                        />

                        <Space style={{ textAlign: 'left' }} direction="vertical" className="therapist-detail">
                            <Text>{item?.name}</Text>
                            <Text>
                                <Text strong>฿ {item?.price}</Text>
                                {` /${item?.duration}`}
                            </Text>
                        </Space>
                    </Space>
                </Col>
                <Col flex="none">
                    <Button>{textSelect}</Button>
                </Col>
            </Row>
        </Card>
    );

    const therapistFiltered = useMemo(() => therapistOptions?.filter((item) => Boolean(item?.display_status)));

    useEffect(() => {
        if (!serviceId) return;
        formInstance.setFieldsValue({ service_id: parseInt(serviceId) });
    }, [formInstance, serviceId]);

    return (
        <>
            <PageMobile isPageHeader customBottomBar={<BookingBottomBar userLatLong={[lat, long]} />}>
                <FormSubmit form={formInstance} layout="vertical" wrapperCol={{ span: 24 }} labelCol={{ span: 24 }} showButtonSubmit={false}>
                    <Control type="input" hidden name="voucher" />
                    <Control type="input" hidden name="lat" />
                    <Control type="input" hidden name="long" />
                    <Control type="input" hidden name="total_price" />

                    <div style={{ rowGap: 16, display: 'flex', flexDirection: 'column' }}>
                        <CardStyled>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>{textServiceDetail}</Text>
                            </div>
                            {masseusesId ? (
                                <Card style={{ background: '#f3fbfa', marginBottom: 12 }} bodyStyle={{ padding: 8 }}>
                                    {isLoadingDetailTherapist ? (
                                        <Skeleton
                                            avatar
                                            paragraph={{
                                                rows: 1,
                                            }}
                                            active={isLoadingDetailTherapist}
                                        />
                                    ) : (
                                        <TherapistInfo
                                            hiddenTag
                                            rating={detailTherapist?.rating}
                                            useAvatar
                                            avatarSize={40}
                                            photo={detailTherapist?.profile_picture}
                                            fullName={detailTherapist?.full_name}
                                            align="middle"
                                            therapisDetail={
                                                <div>
                                                    <DynamicIcon iconName="heartbeat" width={14} height={14} />
                                                    <Text type="secondary">{`${detailTherapist?.booking_count}+ Served`}</Text>
                                                </div>
                                            }
                                        />
                                    )}
                                </Card>
                            ) : (
                                <Control
                                    type="select"
                                    name="masseuse_id"
                                    required
                                    label={textTherapist}
                                    loading={isLoadingTherapistOptions}
                                    options={therapistFiltered}
                                    isMobile
                                    dropdownMatchSelectWidth={false}
                                    optionLabelProp="label"
                                    customRender={renderTherapistSelectOption}
                                    prefix={<DynamicIcon iconName="heartbeat" style={{ color: '#d9d9d9' }} />}
                                />
                            )}
                            <Control
                                type="select"
                                name="service_id"
                                required
                                label={textService}
                                options={servicesOptions}
                                selectValue="id"
                                selectLabel="name"
                                optionLabelProp="label"
                                loading={isLoadingServiceOptions}
                                isMobile
                                prefix={<DynamicIcon iconName="heartbeat" style={{ color: '#d9d9d9' }} />}
                                formItemObj={{ style: { marginBottom: 0 } }}
                                customRender={renderServiceSelectOption}
                            />
                        </CardStyled>
                        <CardStyled>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>{textLocationOrder}</Text>
                            </div>
                            <Control
                                required
                                type="input"
                                label={textLocation}
                                name="user_address"
                                readOnly
                                onClick={() => setVisibleMaps(true)}
                                formItemObj={{
                                    extra: (
                                        <Alert
                                            style={{
                                                background: '#DFF8F5',
                                                padding: '4px 8px',
                                                fontSize: 12,
                                                marginTop: 8,
                                            }}
                                            description="Please provide your accurate address so that the therapist can find you without any issues"
                                            type="info"
                                            icon={
                                                <InfoCircleOutlined
                                                    style={{
                                                        color: 'var(--primary-color)',
                                                        width: 16,
                                                        height: 16,
                                                    }}
                                                />
                                            }
                                            showIcon
                                        />
                                    ),
                                }}
                            />
                            <Control type="input" label={textNotes} name="notes" formItemObj={{ style: { marginBottom: 0 } }} />
                        </CardStyled>

                        <CardStyled>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>{textAdditionalInformation}</Text>
                            </div>
                            <Control type="input" label={textName} name="full_name" />

                            <Form.Item
                                extra={
                                    <Alert
                                        style={{
                                            background: '#DFF8F5',
                                            padding: '4px 8px',
                                            fontSize: 12,
                                            marginTop: 8,
                                        }}
                                        description="Enter your phone to reorder fast and review past services."
                                        type="info"
                                        icon={
                                            <InfoCircleOutlined
                                                style={{
                                                    color: 'var(--primary-color)',
                                                    width: 16,
                                                    height: 16,
                                                }}
                                            />
                                        }
                                        showIcon
                                    />
                                }
                                label={textPhoneNumber}
                                name="phone_number"
                                required
                            >
                                <Input placeholder="e.g. 661234567890" />
                            </Form.Item>
                        </CardStyled>
                    </div>
                </FormSubmit>
            </PageMobile>

            {visibleMaps && (
                <Location formInstance={formInstance} visible={visibleMaps} onCancel={() => setVisibleMaps(false)} valueLat={lat} valueLng={long} />
            )}
        </>
    );
}

function _Booking() {
    return (
        <BookingContextProvider>
            <Booking />
        </BookingContextProvider>
    );
}

export default _Booking;
